function toPositiveId(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function trimmedName(value) {
  if (typeof value !== 'string') return '';
  return value.trim();
}

/** Match header chip: "Team A: Mumbai Indians (593)" or "Team A (593)" when the name is missing. */
export function sideChipLabel(meta, side) {
  const idKey = side === 'a' ? 'teama_id' : 'teamb_id';
  const nameKey = side === 'a' ? 'teama_name' : 'teamb_name';
  const id = toPositiveId(meta?.[idKey]);
  if (id == null) return null;
  const prefix = side === 'a' ? 'Team A' : 'Team B';
  const name = trimmedName(meta?.[nameKey]);
  return name ? `${prefix}: ${name} (${id})` : `${prefix} (${id})`;
}

/** Display name for a franchise on this match. Falls back to Team A/B (id). */
export function franchiseLabel(meta, teamId) {
  const id = toPositiveId(teamId);
  const teamA = toPositiveId(meta?.teama_id);
  const teamB = toPositiveId(meta?.teamb_id);
  if (id != null && teamA != null && id === teamA) {
    return trimmedName(meta?.teama_name) || `Team A (${teamA})`;
  }
  if (id != null && teamB != null && id === teamB) {
    return trimmedName(meta?.teamb_name) || `Team B (${teamB})`;
  }
  return id != null ? `Team (${id})` : 'Team';
}

export function opponentTeamId(meta, forTeamId) {
  const teamA = toPositiveId(meta?.teama_id);
  const teamB = toPositiveId(meta?.teamb_id);
  const side = toPositiveId(forTeamId);
  if (teamA != null && side === teamA) return teamB;
  if (teamB != null && side === teamB) return teamA;
  return null;
}

export function sortConfigsForMatch(configs, meta) {
  const list = Array.isArray(configs) ? [...configs] : [];
  const teamA = toPositiveId(meta?.teama_id);
  const teamB = toPositiveId(meta?.teamb_id);
  const rank = (row) => {
    const id = toPositiveId(row?.for_team_id);
    if (id != null && id === teamA) return 0;
    if (id != null && id === teamB) return 1;
    return 2;
  };
  list.sort((a, b) => {
    const bySide = rank(a) - rank(b);
    if (bySide !== 0) return bySide;
    return (toPositiveId(a?.for_team_id) || 0) - (toPositiveId(b?.for_team_id) || 0);
  });
  return list;
}

export function normalizeLineupPayload(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw.data != null && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data : raw;
  const displaySrc = data.player_display_by_id ?? raw.player_display_by_id;
  const player_display_by_id =
    displaySrc != null && typeof displaySrc === 'object' && !Array.isArray(displaySrc) ? displaySrc : {};
  return {
    ...data,
    player_display_by_id,
    on_demand_sync_attempted: data.on_demand_sync_attempted ?? raw.on_demand_sync_attempted,
    sync_error: data.sync_error ?? raw.sync_error,
    sync_skipped: data.sync_skipped ?? raw.sync_skipped,
  };
}

export function buildInitialSystemFive(config) {
  const effective = config?.effective_system_player_ids;
  if (Array.isArray(effective) && effective.length === 5) return effective.map((id) => Number(id));
  const auto = config?.auto_system_player_ids;
  if (Array.isArray(auto) && auto.length === 5) return auto.map((id) => Number(id));
  return [null, null, null, null, null];
}

function cleanEntries(snapshot) {
  if (!Array.isArray(snapshot)) return [];
  const seen = new Set();
  const rows = [];
  snapshot.forEach((entry) => {
    const playerId = toPositiveId(entry?.player_id);
    if (playerId == null || seen.has(playerId)) return;
    seen.add(playerId);
    rows.push({ ...entry, player_id: playerId });
  });
  return rows;
}

/** System five labels/scores: manual snapshot when it has rows, otherwise auto. */
export function systemSelectedEntries(config) {
  const manual = cleanEntries(config?.manual_system_players_snapshot);
  if (manual.length > 0) return manual;
  return cleanEntries(config?.auto_system_players_snapshot);
}

export function systemSnapshotSource(config) {
  if (cleanEntries(config?.manual_system_players_snapshot).length > 0) return 'manual';
  if (cleanEntries(config?.auto_system_players_snapshot).length > 0) return 'auto';
  return 'none';
}

/**
 * User pick table.
 * A saved manual order renders manual_user_pick_snapshot only.
 * Auto snapshot players that were left out of that order stay off this table.
 */
export function userPickEntries(config) {
  const manualOrder = Array.isArray(config?.manual_user_pick_order) ? config.manual_user_pick_order : null;
  const manualActive = manualOrder != null && manualOrder.length > 0;
  if (!manualActive) return cleanEntries(config?.auto_user_pick_snapshot);

  const manualSnapshot = cleanEntries(config?.manual_user_pick_snapshot);
  if (manualSnapshot.length > 0) return manualSnapshot;

  const autoById = new Map(cleanEntries(config?.auto_user_pick_snapshot).map((entry) => [entry.player_id, entry]));
  const seen = new Set();
  const rows = [];
  manualOrder.forEach((id) => {
    const playerId = toPositiveId(id);
    if (playerId == null || seen.has(playerId)) return;
    seen.add(playerId);
    rows.push(autoById.get(playerId) || { player_id: playerId, impact: null });
  });
  return rows;
}

export function userPickSnapshotSource(config) {
  const manualOrder = Array.isArray(config?.manual_user_pick_order) && config.manual_user_pick_order.length > 0;
  if (manualOrder) return 'manual';
  if (cleanEntries(config?.auto_user_pick_snapshot).length > 0) return 'auto';
  return 'none';
}

export function findSnapshotEntry(lists, playerId) {
  const id = toPositiveId(playerId);
  if (id == null) return null;
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    const hit = list.find((entry) => toPositiveId(entry?.player_id) === id);
    if (hit) return { ...hit, player_id: id };
  }
  return null;
}

export function orderedIdsByPriority(rows, priorities) {
  const list = (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      player_id: toPositiveId(row?.player_id),
      priority: Number(priorities?.[row?.player_id]) || 0,
    }))
    .filter((row) => row.player_id != null);
  list.sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority;
    return a.player_id - b.player_id;
  });
  return list.map((row) => row.player_id);
}

export function formatImpactValue(value, digits = 2) {
  if (value == null || value === '' || Number.isNaN(Number(value))) return '—';
  return Number(value).toFixed(digits);
}

export function formatImpactCount(value) {
  if (value == null || value === '') return '—';
  return String(value);
}

function playerTeamId(player) {
  const value = player?.tid ?? player?.team_id ?? player?.franchise_id ?? player?.t_id ?? player?.for_team_id;
  return value != null ? Number(value) : null;
}

/**
 * Opponent picker: playing eleven for the opponent franchise, plus ids already
 * stored on this config so a lagged squad can still be re-selected.
 */
export function buildOpponentPickerPool(playingEleven, opponentId, config) {
  const byId = new Map();
  let squad = [];

  if (Array.isArray(playingEleven) && playingEleven.length > 0) {
    if (opponentId != null) {
      squad = playingEleven.filter((player) => {
        const teamId = playerTeamId(player);
        if (teamId == null) return true;
        return teamId === opponentId;
      });
      if (squad.length === 0) squad = [...playingEleven];
    } else {
      squad = [...playingEleven];
    }
    squad.forEach((player) => {
      const id = toPositiveId(player?.player_id);
      if (id == null) return;
      byId.set(id, { ...player, player_id: id, _pickerFromSquad: true });
    });
  }

  const addSynthetic = (rawId) => {
    const id = toPositiveId(rawId);
    if (id == null || byId.has(id)) return;
    byId.set(id, { player_id: id, _pickerFromSquad: false });
  };

  cleanEntries(config?.auto_system_players_snapshot).forEach((entry) => addSynthetic(entry.player_id));
  cleanEntries(config?.manual_system_players_snapshot).forEach((entry) => addSynthetic(entry.player_id));
  [config?.auto_system_player_ids, config?.manual_system_player_ids, config?.effective_system_player_ids].forEach(
    (ids) => {
      if (Array.isArray(ids)) ids.forEach(addSynthetic);
    },
  );

  const squadOrder = squad.map((player) => toPositiveId(player?.player_id)).filter((id) => id != null);
  const seen = new Set(squadOrder);
  const ordered = [...squadOrder];
  [...byId.keys()]
    .filter((id) => !seen.has(id))
    .sort((a, b) => a - b)
    .forEach((id) => {
      ordered.push(id);
      seen.add(id);
    });

  return ordered.map((id) => byId.get(id)).filter(Boolean);
}

export function getDisplayEntry(displayById, playerId) {
  if (displayById == null || playerId == null || typeof displayById !== 'object') return null;
  const record = displayById[String(playerId)] ?? displayById[Number(playerId)];
  if (record && typeof record === 'object' && !Array.isArray(record)) return record;
  return null;
}

export function playerPrimaryName({ snapshot, displayEntry, squadName, playerId }) {
  if (snapshot?.player_name) return String(snapshot.player_name);
  if (snapshot?.player_short_name) return String(snapshot.player_short_name);
  if (displayEntry?.name) return String(displayEntry.name);
  if (displayEntry?.short_name) return String(displayEntry.short_name);
  if (squadName) return squadName;
  return `Player ${playerId}`;
}
